// libs
import replaceAsciiEmojis from 'emoji-shortcuts'
import * as React from 'react'

// interfaces / constants
import { CLASS_PREFIX } from 'src/constants/'
import { IMoreOption, MORE_OPTIONS } from 'src/constants/more_button'

// classes
import Avatar from 'src/components/avatar/avatar'
import LinkPreview from 'src/components/link_preview/link_preview'
import { MSGBOX_BUTTON_TYPE_CANCEL, MSGBOX_BUTTON_TYPE_DANGER } from 'src/components/message_box/message_box'
import MessageBoxWithModal from 'src/components/message_box/message_box_with_modal'
import NavigationItem from 'src/components/navigation_item/navigation_item'
import Like from 'src/components/post/shared/comments/like/like'
import More from 'src/components/post/shared/more_button/more'
import { THUMBNAIL_SIZE_TYPE_LARGE_MEDIUM_LARGE, THUMBNAIL_SIZE_TYPE_XLARGE } from 'src/components/thumbnail/thumbnail'
import ThumbnailGallery from 'src/components/thumbnail_gallery/thumbnail_gallery'

// helpers
import { CommentFragmentFragment } from '@lopo-tech/lopo-api'
import { textResources } from 'src/lang/de'
import { getSponsorBadge } from 'src/utils/badges'
import DateFormatter from 'src/utils/date_formatter/date_formatter'
import { makeHTMLLinksFromText } from 'src/utils/string/string'
import { UrlUtils } from 'src/utils/url/url'
import UserOnboardingCTA from '../../../../containers/smart_components/user_onboarding_cta/user_onboarding_cta'
import './comment.scss'

const cls = CLASS_PREFIX + 'comment'
const clsEditedHint = `${cls}__edited-hint`
const clsContentsWrapper = `${cls}__contents-wrapper`
const labels = textResources.comments

interface IProps {
  comment: CommentFragmentFragment;
  editComment?: (comment: CommentFragmentFragment) => void;
  deleteComment?: (id: string) => void;
  likeToggle?: (id: string) => void;
  isLoggedIn: boolean;
}

interface IState {
  openModal: boolean;
}

export default class Comment extends React.PureComponent<IProps, IState> {
  constructor (props: IProps) {
    super(props)
    this.closeMessageBox = this.closeMessageBox.bind(this)
    this.handleCancelClick = this.handleCancelClick.bind(this)
    this.handleDeleteClick = this.handleDeleteClick.bind(this)
    this.handleEditClick = this.handleEditClick.bind(this)
    this.handleLike = this.handleLike.bind(this)
    this.handleMoreActionClick = this.handleMoreActionClick.bind(this)
    this.renderLike = this.renderLike.bind(this)
    this.renderEditedHint = this.renderEditedHint.bind(this)
    this.state = { openModal: false }
  }

  public render () {
    const { attachments, author, createdAt, message, canDestroy, canUpdate } = this.props.comment
    const { openModal } = this.state
    const profileLink = author && UrlUtils.getProfileFrontendPath(author.slug, author.__typename)
    const imageUrls = attachments.map((attachment) => attachment.file.previewUrl)
    // TODO: Why can message be null?
    const parsedMessage = this.parseMessage(message ?? '')
    const options: IMoreOption[] = []
    if (canUpdate.value) {
      options.push(MORE_OPTIONS.EDIT_COMMENT)
    }
    if (canDestroy.value) {
      options.push(MORE_OPTIONS.DELETE_COMMENT)
    }
    const isDisabled = !author?.canShow.value
    return (
      <div className={cls + ' ' + CLASS_PREFIX + 'popover__fix_comment'}>
        <NavigationItem
          target="_self"
          url={profileLink}
          className={cls + '__avatar'}
        >
          <Avatar
            image={author?.avatar.previewUrl}
            size={THUMBNAIL_SIZE_TYPE_LARGE_MEDIUM_LARGE}
            rounded={false}
            badge={
              author?.shapeSponsorshipLevel &&
              getSponsorBadge(author.shapeSponsorshipLevel)
            }
          />
        </NavigationItem>
        <div className={cls + '__body'}>
          <div className={cls + '__contents'}>
            <div className={clsContentsWrapper}>
              <div className={cls + '__message'}>
                <UserOnboardingCTA active={isDisabled}>
                  <NavigationItem
                    target="_self"
                    url={profileLink}
                    className={cls + '__author-name'}
                  >
                    <div>{author?.name || ''}</div>
                  </NavigationItem>
                </UserOnboardingCTA>
                <div className={cls + '__message-content'}>
                  <div
                    className={cls + '__message-html'}
                    dangerouslySetInnerHTML={{ __html: parsedMessage }}
                  />
                  {attachments.length > 0 && (
                    <div className={cls + '__images'}>
                      <ThumbnailGallery
                        imageUrls={imageUrls}
                        size={THUMBNAIL_SIZE_TYPE_XLARGE}
                        fold
                      />
                    </div>
                  )}
                </div>
              </div>

              {this.renderEditedHint()}
            </div>
            {this.renderLinkPreview()}
          </div>
          <div className={cls + '__footer'}>
            {this.props.likeToggle && (
              <div className={cls + '__reactions'}>{this.renderLike()}</div>
            )}
            <div className={cls + '__date'}>
              {DateFormatter.asTimeAgo(createdAt)}
            </div>
            <div className={cls + '__more-button'}>
              {(canDestroy.value || canUpdate.value) && (
                <More
                  disabled={false}
                  onChange={this.handleMoreActionClick}
                  options={options}
                />
              )}
            </div>
          </div>
        </div>
        {openModal && (
          <MessageBoxWithModal
            buttons={[
              {
                onClick: this.handleDeleteClick,
                type: MSGBOX_BUTTON_TYPE_DANGER
              },
              {
                onClick: this.closeMessageBox,
                type: MSGBOX_BUTTON_TYPE_CANCEL
              }
            ]}
            onClose={this.closeMessageBox}
            title={labels.remove}
          >
            <p>{labels.sureToRemove}</p>
          </MessageBoxWithModal>
        )}
      </div>
    )
  }

  private renderLinkPreview () {
    if (!this.props.comment.oglink) {
      return null
    }
    const { author, description, image, title, url } = this.props.comment.oglink

    return (
      <div className={cls + '__link-preview'}>
        <LinkPreview
          author={author}
          description={description || ''}
          displayUrl={url}
          imageUrl={image?.previewUrl}
          target={'_blank'}
          title={title}
          url={url}
        />
      </div>
    )
  }

  private closeMessageBox () {
    this.setState({ openModal: false })
  }

  private handleCancelClick () {
    this.closeMessageBox()
  }

  private handleDeleteClick () {
    if (this.props.deleteComment) {
      this.props.deleteComment(this.props.comment.id)
    }
    this.closeMessageBox()
  }

  private handleEditClick () {
    const comment = this.props.comment
    if (this.props.editComment) {
      this.props.editComment(comment)
    }
  }

  private handleLike () {
    const { id } = this.props.comment
    if (this.props.likeToggle) {
      this.props.likeToggle(id)
    }
  }

  private handleMoreActionClick (selected: IMoreOption) {
    if (selected.value === MORE_OPTIONS.DELETE_COMMENT.value) {
      this.setState({ openModal: true })
    }
    if (selected.value === MORE_OPTIONS.EDIT_COMMENT.value) {
      this.handleEditClick()
    }
  }

  private parseMessage (message: string) {
    return makeHTMLLinksFromText(replaceAsciiEmojis(message))
  }

  private renderLike (): JSX.Element | null {
    const { canReact, reactions } = this.props.comment

    if (reactions.availableReactions.length === 0) {
      return null
    }

    return (
      <Like
        active={!!reactions.ownReaction}
        likes={reactions.receivedReactions.reduce((acc, { count }) => acc + count, 0)}
        onClick={this.handleLike}
        canBookmark={!canReact.value}
      />
    )
  }

  private renderEditedHint (): JSX.Element | null {
    const { createdAt, updatedAt } = this.props.comment

    if (updatedAt === '' || createdAt === updatedAt) {
      return null
    }

    return (
      <div className={clsEditedHint}>
        {labels.edited(DateFormatter.asTimeAgo(updatedAt))}
      </div>
    )
  }
}
